import * as React from 'react';
import { withRouter, match } from 'react-router';
import styled from 'styled-components';
import { Location, History } from 'history';
import GMapsWrapper, { WrappedGmapObj } from 'google-maps-wrapper';
import WrappedMapBase from 'google-maps-wrapper';

import socket from '../../socket';
import math_helpers from '../../helpers/math_helpers';
import { fetchPOISet, POI, POIType, NewPOI } from '../../helpers/poi_helpers';
import { getIconByPOIType } from '../../helpers/icon_helpers';
import { createPOI } from '../../helpers/api_helpers';

import withVehicles, { Vehicle } from '../../VehiclesHOC';
import BusesOverlay from '../base/BusesOverlay';
import default_config from '../../default_config';

declare global {
    interface Window {
        google: any;
        createPOI: any;
        onresize: (ev: any) => void;
    }
}

interface MainViewProps {
    history: History;
    location: Location;
    match: match;
    config: { [key:string]: any };
    vehicles: { [key:string]: Vehicle };
}

interface LatLng {
    lat: number;
    lng: number;
}
interface LatLngBounds {
    north: number;
    west: number;
    east: number;
    south: number;
}


interface MainViewState {
    center: LatLng|null;
    bounds: LatLngBounds|null;
    selected_poi_set_id: number;
    drawn_poi_set_id: number;
    zoom: number;
    my_location: LatLng|null;
}

class MainView extends React.Component<MainViewProps, MainViewState> {
    container: any;
    map_ref: WrappedMapBase;
    drawn_poi: { [key:string]: WrappedGmapObj } = {};
    bus_overlay_ref: BusesOverlay | null = null;

    constructor(props: MainViewProps) {
        super(props);

        this.state = {
            center: null,
            bounds: null,
            my_location: null,
            selected_poi_set_id: -1,
            drawn_poi_set_id: -1,
            zoom: -1
        };

        this.container = styled.div `
            position: fixed;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
        `;
    }

    componentDidMount() {
        this.updateStateFromURL();
        if (socket.isConnected() === false) {
            socket.connect(this.props.config.notifications.url);
        }
        window.onresize = () => {
            this.updateStateFromURL();
        };
    }

    componentDidUpdate(prev_props: MainViewProps, prev_state: MainViewState) {
        if (!this.map_ref) {
            return;
        }
        if (this.state.drawn_poi_set_id === -1 && this.state.selected_poi_set_id > -1 && this.state.drawn_poi_set_id !== this.state.selected_poi_set_id) {
            this.setState({
                drawn_poi_set_id: this.state.selected_poi_set_id
            });
            this.updatePOIs(this.state.selected_poi_set_id);
        }
        if (this.map_ref.initialized && this.state.my_location !== null && prev_state.my_location !== this.state.my_location) {
            this.updateMyLocation(this.state.my_location);
        }
        if (this.map_ref.initialized && this.state.bounds && prev_state.bounds !== this.state.bounds) {
            //Bounds have updated.
            this.map_ref.map.fitBounds(this.state.bounds);
        }
        let vehicles = this.props.vehicles;
        this.updateBuses(vehicles, true, prev_props.vehicles);
    }

    updateBuses(vehicles: { [key:string]: Vehicle }, transition: boolean, prev_vehicles: { [key:string]: Vehicle } = {}) {
        let v_ids = Object.keys(vehicles);
        v_ids.forEach((v_id) => {
            let vehicle = vehicles[v_id];
            if (vehicle === prev_vehicles[v_id]) {
                return;
            }

            if (this.bus_overlay_ref !== null) {
                if (vehicle.alive === false) {
                    this.bus_overlay_ref.removeBus(vehicle);
                } else {
                    this.bus_overlay_ref.updateBus(vehicle, transition);
                }
            }
        });
    }

    updateStateFromURL() {
        let params = new URLSearchParams(this.props.location.search);

        let center: LatLng|null = this.state.center;
        let zoom: number = this.state.zoom;
        let bounds: LatLngBounds|null = this.state.bounds;
        let poi_set_id: number = this.state.selected_poi_set_id;
        let my_location: LatLng|null = this.state.my_location;

        let poi_set_param = params.get('poi_set');
        let poi_set_value = (poi_set_param) ? parseInt(poi_set_param, 10) : this.state.selected_poi_set_id;
        if (math_helpers.isNumeric(poi_set_value)) {
            poi_set_id = poi_set_value;
        }

        let my_location_param = params.get('my_location');
        if (my_location_param) {
            let values = my_location_param.split(',').map(value => parseFloat(value));
            if (values.length === 2 && values.every(value => math_helpers.isNumeric(value))) {
                my_location = {
                    lat: values[0],
                    lng: values[1]
                };
            }
        }

        let bounds_param = params.get('bounds');
        if (bounds_param) {
            //Bounding parameter expected in format "float,float,float,float" representing North,West,South,East.
            let values = bounds_param.split(',').map(value => parseFloat(value));
            if (values.length === 4 && values.every(value => math_helpers.isNumeric(value))) {
                bounds = {
                    north: values[0],
                    west: values[1],
                    south: values[2],
                    east: values[3]
                };
            }
        }

        if (!bounds) {
            let center_param = params.get('center');
            if (center_param) {
                let values = center_param.split(',').map(value => parseFloat(value));
                if (values.length === 2 && values.every(value => math_helpers.isNumeric(value))) {
                    center = {
                        lat: values[0],
                        lng: values[1]
                    };
                }
            }

            let zoom_param = params.get('zoom');
            let zoom_value = (zoom_param) ? parseInt(zoom_param, 10) : this.state.zoom;
            if (math_helpers.isNumeric(zoom_value)) {
                zoom = zoom_value;
            }
        }

        let new_state = {
            center: center || this.state.center,
            zoom: zoom,
            bounds: bounds || this.state.bounds,
            selected_poi_set_id: poi_set_id,
            my_location: my_location
        };
        this.setState(new_state);
    }

    updatePOIs(set_id: number) {
        if (set_id === -1) {
            console.error("Got updatePOI call with no poi_set_id.");
            return;
        }
        if (!this.map_ref || !this.map_ref.initialized) {
            return;
        }
        fetchPOISet(set_id).then((new_pois: POI[]) => {
            //Clear all existing POIs.
            Object.keys(this.drawn_poi).forEach((poi_key) => {
                let gmaps_obj = this.drawn_poi[poi_key];
                gmaps_obj.remove();
            });
            this.drawn_poi = {};
            //Add new POIs.

            new_pois.forEach((poi) => {
                let marker_options: LooseObject = {
                    position: { lat: poi.position.y, lng: poi.position.x },
                    icon: getIconByPOIType(poi.type),
                    visible: true,
                    clickable: false,
                };
                if (poi.name) {
                    marker_options.label = poi.name;
                }
                if (poi.type === "text") {
                    marker_options.zIndex = 9000;
                }
                this.map_ref.setMarker(poi.id, marker_options).catch((err) => {
                    //Couldn't create the marker.
                    //TODO: Silent slack msg.
                    return;
                });
            });
        }).catch((err: any) => {
            //TODO: Silent slack msg.
            console.error(err);
            return;
        });
    }

    updateMyLocation(pos: LatLng) {
        if (!this.map_ref) { return; }
        let marker_options = {
            position: pos,
            icon: getIconByPOIType("my_location"),
            label: "",
            visible: true,
            clickable: false,
            zIndex: 10000
        };
        this.map_ref.setMarker("my_location", marker_options).catch((err) => {
            //Couldn't create the marker.
            //TODO: Silent slack msg.
            return;
        });
    }

    mapInitializedCB() {
        this.updatePOIs(this.state.selected_poi_set_id);
        if (this.state.bounds) {
            //Bounds have updated.
            this.map_ref.map.fitBounds(this.state.bounds);
        }
        if (this.state.my_location) {
            this.updateMyLocation(this.state.my_location);
        }
    }

    mapIdleCB() {
        this.updateBuses(this.props.vehicles, false);
    }

    createPOICB(values: LooseObject) {
        let lat: number;
        let lng: number;
        let name: string;
        let type: POIType = values.type;


        lat = parseFloat(values.lat);
        if (!math_helpers.isNumeric(lat) || lat < -90 || lat > 90) {
            console.error("Invalid latitude.");
            return;
        }

        lng = parseFloat(values.lng);
        if (!math_helpers.isNumeric(lng) || lng < -180 || lng > 180) {
            console.error("Invalid longitude.");
            return;
        }

        if (values.name.trim().length > 0) {
            name = values.name.trim();
        } else {
            console.error("Invalid name.");
            return;
        }

        let new_poi: NewPOI = {
            name: name,
            position: {
                crs: "wgs84",
                x: lng,
                y: lat
            },
            type: type
        };
        createPOI(new_poi);
    }

    logCoord(latLng: google.maps.LatLng): void {
        console.log(latLng.lng().toFixed(8), latLng.lat().toFixed(8));
    }

    render() {
        // console.log("this.props.vehicles:", this.props.vehicles);
        return (
            <this.container>
                <GMapsWrapper
                    api_key={this.props.config.google.maps.api_key}
                    id="my_map"
                    defaultOptions={{
                        mapTypeControl: false,
                        streetViewControl: false,
                        zoomControl: false,
                        fullscreenControl: false
                    }}
                    styles={default_config.google.maps.styles}
                    defaultCenter={this.props.config.default_map_position}
                    defaultZoom={this.props.config.default_map_zoom}
                    ref={map_ref => this.map_ref = map_ref || this.map_ref}
                    initializedCB={() => { this.mapInitializedCB(); }}
                    onIdle={() => this.mapIdleCB()}
                    onClick={(e) => { this.logCoord(e.latLng); }}
                />
                <BusesOverlay
                    map_ref={this.map_ref}
                    ref={bus_overlay_ref => this.bus_overlay_ref = bus_overlay_ref}
                />
            </this.container>
        );
    }
}

export default withVehicles(withRouter(MainView));



// WEBPACK FOOTER //
// ./src/components/views/MainView.tsx